/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../common';
var sizeToClassNameMap = {
  m: 'ouiLoadingChart--medium',
  l: 'ouiLoadingChart--large',
  xl: 'ouiLoadingChart--xLarge'
};
export var SIZES = keysOf(sizeToClassNameMap);
export var OuiLoadingChart = ({
  size = 'm',
  mono = false,
  className,
  ...rest
}) => {
  const classes = classNames('ouiLoadingChart', {
    'ouiLoadingChart--mono': mono
  }, className, sizeToClassNameMap[size]);
  return <span className={classes} {...rest}>
      <span className="ouiLoadingChart__bar" />
      <span className="ouiLoadingChart__bar" />
      <span className="ouiLoadingChart__bar" />
      <span className="ouiLoadingChart__bar" />
    </span>;
};
OuiLoadingChart.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  size: PropTypes.oneOf(["m", "l", "xl"]),
  mono: PropTypes.bool
};
try {
  OuiLoadingChart.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3077135706/src/src/components/loading/loading_chart.tsx',
    description: '',
    displayName: 'OuiLoadingChart',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      size: {
        defaultValue: {
          value: 'm'
        },
        description: '',
        name: 'size',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/loading/loading_chart.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"m" | "l" | "xl"',
          value: [{
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      mono: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'mono',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/loading/loading_chart.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}